/**
 * web/src/lib/forms/draft.ts
 * localStorage-backed gate draft autosave + restore.
 * Key: pixel:gate-draft:{workflowId}:{stepIndex}:{userId}.
 * 30-day TTL; lazy GC on read (and via gcDrafts() at app startup).
 * Cleared on successful Approve/Reject/Resubmit (caller invokes clearDraft).
 *
 * userId in the key prevents one reviewer's draft showing up in another
 * reviewer's session if they share a workstation (T-05-03-02).
 */
const PREFIX = 'pixel:gate-draft:';
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface Draft<T> {
  values: T;
  savedAt: number;
  /** Fingerprint of the server draft the edits were made on (gate drafts). */
  base?: string;
}

/**
 * Builds the draft key for a (workflowId, stepIndex, userId) tuple.
 *
 * @param workflowId workflow id
 * @param stepIndex zero-based step index
 * @param userId authenticated user id (Me.id from /api/me)
 * @returns the canonical localStorage key
 * @sideEffects none
 * @idempotent yes
 */
export function key(workflowId: string, stepIndex: number, userId: string): string {
  return `${PREFIX}${workflowId}:${stepIndex}:${userId}`;
}

/**
 * Persists `values` under `k`. Wraps in try/catch so QuotaExceededError
 * (private mode, full storage) doesn't crash the form.
 *
 * @param k canonical draft key (from `key()`)
 * @param values RHF form values to save
 * @param base optional fingerprint of the server draft the edits were made on;
 *        `readDraftOnBase` drops the draft once the server draft changes
 * @sideEffects writes to localStorage
 * @idempotent yes
 */
export function saveDraft<T>(k: string, values: T, base?: string): void {
  try {
    const payload: Draft<T> = { values, savedAt: Date.now(), ...(base != null ? { base } : {}) };
    localStorage.setItem(k, JSON.stringify(payload));
  } catch {
    // localStorage quota or private mode — fail silently
  }
}

/**
 * Reads the persisted draft for `k`. Returns null when missing, expired,
 * or corrupted. Removes expired/corrupted entries on read (lazy GC).
 *
 * @param k canonical draft key
 * @returns parsed values or null
 * @sideEffects may removeItem on TTL expiry / parse failure
 * @idempotent yes — reading twice produces the same result
 */
export function readDraft<T>(k: string): T | null {
  try {
    const raw = localStorage.getItem(k);
    if (!raw) return null;
    const { values, savedAt } = JSON.parse(raw) as Draft<T>;
    if (Date.now() - savedAt > TTL_MS) {
      localStorage.removeItem(k);
      return null;
    }
    return values;
  } catch {
    try {
      localStorage.removeItem(k);
    } catch {
      // ignore
    }
    return null;
  }
}

/**
 * Removes the draft entry for `k` (called after successful approve/reject/resubmit).
 *
 * @param k canonical draft key
 * @sideEffects writes to localStorage
 * @idempotent yes
 */
export function clearDraft(k: string): void {
  try {
    localStorage.removeItem(k);
  } catch {
    // ignore
  }
}

/**
 * Lazy GC — call once at app startup to remove drafts older than TTL.
 * Iterates only over keys with the `pixel:gate-draft:` prefix; unrelated
 * localStorage entries are untouched.
 *
 * @sideEffects writes to localStorage (removeItem on expired entries)
 * @idempotent yes — running it twice in a row removes nothing the second time
 */
export function gcDrafts(): void {
  try {
    const now = Date.now();
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(PREFIX)) continue;
      try {
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        const { savedAt } = JSON.parse(raw) as Draft<unknown>;
        if (now - savedAt > TTL_MS) localStorage.removeItem(k);
      } catch {
        try {
          localStorage.removeItem(k);
        } catch {
          // ignore
        }
      }
    }
  } catch {
    // ignore — localStorage unavailable
  }
}

/**
 * Reads the draft for `k` only if it was made on the same server draft.
 *
 * 2026-10-29: a gate rebuilt on the server (Rebuild drafts, a new Doc sync,
 * another reviewer) must win over edits saved against the OLD server draft —
 * otherwise a reviewer who opened the gate earlier keeps seeing stale copy.
 * A draft saved without a `base` (before this check existed, when every page
 * open autosaved the untouched form) is treated as stale too. Stale drafts are
 * removed.
 *
 * @param k canonical draft key
 * @param base fingerprint of the current server draft
 * @returns the draft's values, or null when missing/expired/stale
 * @sideEffects may removeItem
 */
export function readDraftOnBase<T>(k: string, base: string): T | null {
  const values = readDraft<T>(k);
  if (values == null) return null;
  try {
    const stored = (JSON.parse(localStorage.getItem(k) ?? '{}') as Draft<T>).base;
    if (stored === base) return values;
    localStorage.removeItem(k);
  } catch {
    // ignore
  }
  return null;
}
